import { datadogLogs, HandlerType, StatusType } from "@datadog/browser-logs";

import isLocal from "@de-common/env/isLocal";
import isTest from "./isTest";

let ready = false;
const pendingLogs: Array<{
  level: StatusType;
  message: string;
  messageContext?: object | undefined;
  error?: Error | undefined;
}> = [];

if (!isTest()) {
  datadogLogs.onReady(function () {
    const logger = datadogLogs.logger;

    const handlers: HandlerType[] = [];

    if (isLocal()) {
      handlers.push(HandlerType.console);
    } else {
      handlers.push(HandlerType.http);
    }

    logger.setHandler(handlers);
    logger.setLevel(StatusType.debug);

    ready = true;

    // flush any logs that arrived before the SDK was ready
    for (const pending of pendingLogs) {
      logger[pending.level](pending.message, pending.messageContext, pending.error);
    }
    pendingLogs.length = 0;
  });
}

function getLogger() {
  return ready ? datadogLogs.logger : undefined;
}

// This ensures we always have an error object to pass on further to DataDog if possible
function downcastErrorFromMessage(
  message: unknown,
  originalError?: unknown,
): Error | undefined {
  if (!originalError && message instanceof Error) {
    return message;
  } else if (originalError instanceof Error) {
    return originalError;
  } else if (originalError) {
    warn("Original error is not an instance of Error", { originalError });
  }

  return undefined;
}

function logOrQueue(
  level: StatusType,
  message: unknown,
  messageContext?: object,
  originalError?: unknown,
) {
  const error = downcastErrorFromMessage(message, originalError);
  const logger = getLogger();
  if (logger) {
    logger[level](String(message), messageContext, error);
  } else {
    pendingLogs.push({ level, message: String(message), messageContext, error });
  }
}

const debug = (message: unknown, messageContext?: object, originalError?: unknown) => {
  logOrQueue(StatusType.debug, message, messageContext, originalError);
};

const info = (message: unknown, messageContext?: object, originalError?: unknown) => {
  logOrQueue(StatusType.info, message, messageContext, originalError);
};

const warn = (message: unknown, messageContext?: object, originalError?: unknown) => {
  logOrQueue(StatusType.warn, message, messageContext, originalError);
};

const error = (message: unknown, messageContext?: object, originalError?: unknown) => {
  logOrQueue(StatusType.error, message, messageContext, originalError);
};

export default {
  debug,
  info,
  warn,
  error,
};
